/**
 * Google APIを使用するためのUtilです
 */

export interface GoogleApiErrorType {
  name: string;
  message: string;
  code?: string;
  details?: unknown;
}

export function createGoogleApiError(message: string, code?: string, details?: unknown): GoogleApiErrorType {
  return {
    name: 'GoogleApiError',
    message,
    code,
    details
  };
}

// 型定義
export type GoogleGeocodingResponse = {
  status: string;
  results: {
    place_id: string;
    formatted_address: string;
    address_components: { long_name: string; short_name: string; types: any }[];
    geometry: {
      location: { lat: number; lng: number };
      location_type: string;
      viewport: {
        south?: number;
        west?: number;
        north?: number;
        east?: number;
      };
      bounds?: {
        south: number;
        west: number;
        north: number;
        east: number;
      };
    };
    plus_code?: { compound_code: string; global_code: string };
    types: string[];
  }[];
};

const geocodeUsingJavaScriptAPI = (lat: number, lng: number): Promise<GoogleGeocodingResponse> => {
  return new Promise<GoogleGeocodingResponse>((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      reject(createGoogleApiError('Google Maps APIが読み込まれていません'));
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK') {
        const formattedResults = results.map(result => {
          const formatted = {
            place_id: result.place_id,
            formatted_address: result.formatted_address,
            address_components: result.address_components.map(comp => ({
              long_name: comp.long_name,
              short_name: comp.short_name,
              types: comp.types
            })),
            geometry: {
              location: {
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng()
              },
              location_type: result.geometry.location_type,
              viewport: {}
            },
            types: result.types
          };

          if (result.geometry.viewport) {
            formatted.geometry.viewport = {
              south: result.geometry.viewport.getSouthWest().lat(),
              west: result.geometry.viewport.getSouthWest().lng(),
              north: result.geometry.viewport.getNorthEast().lat(),
              east: result.geometry.viewport.getNorthEast().lng()
            };
          }

          return formatted;
        });

        resolve({
          status,
          results: formattedResults
        });
      } else {
        let errorMessage = 'ジオコーディングに失敗しました。';

        switch (status) {
          case 'ZERO_RESULTS':
            errorMessage = '指定された座標に対応する住所が見つかりませんでした。';
            break;
          case 'OVER_QUERY_LIMIT':
            errorMessage = 'APIの使用制限を超えました。';
            break;
          case 'REQUEST_DENIED':
            errorMessage = 'リクエストが拒否されました。';
            break;
          case 'INVALID_REQUEST':
            errorMessage = '無効なリクエストです。';
            break;
          case 'UNKNOWN_ERROR':
            errorMessage = 'サーバーエラーが発生しました。';
            break;
          default:
            errorMessage = `不明なエラーが発生しました: ${status}`;
        }

        reject(createGoogleApiError(errorMessage, status));
      }
    });
  });
};

export const fetchCityName = (lat: number, lng: number): Promise<GoogleGeocodingResponse> => {
  if (window.google?.maps) {
    return geocodeUsingJavaScriptAPI(lat, lng);
  }
  return Promise.reject(
    createGoogleApiError(
      'Google Maps APIが読み込まれていません。ページを再読み込みするか、管理者にお問い合わせください。',
      'API_NOT_LOADED'
    )
  );
};
